import _ from 'lodash';

/**
 * Quick filters on a scorecard are merged into the query for EVERY KPI, so a
 * filter field must exist — with a compatible type — in every dataset the
 * scorecard's KPIs consume. These pure helpers enforce that:
 *
 * - {@link buildQuickFilterFieldRedList} powers the config-time restriction
 *   (Requirement 1) by producing the set of field names that are NOT common to
 *   all datasets, which is fed straight into `FilterPlatesProvider.fieldRedList`.
 * - {@link getQuickFilterCompatibility} powers the view-time check (Requirement
 *   2) by reporting whether an existing filter is still safe against the
 *   scorecard's current datasets.
 */

const NUMERIC_FIELD_TYPES: FleetOps.FieldType[] = ['float', 'int', 'long'];
const DATE_FIELD_TYPES: FleetOps.FieldType[] = ['date', 'dateText'];

/**
 * Collapses interchangeable field types into a single category so that a filter
 * built against, say, an `int` field is considered compatible with a `long`
 * field of the same name. This mirrors how `NumberFilter`/`DateFilter` plates
 * already group these types.
 */
export const getFieldTypeCategory = (type: FleetOps.FieldType): string => {
  if (NUMERIC_FIELD_TYPES.includes(type)) {
    return 'number';
  }
  if (DATE_FIELD_TYPES.includes(type)) {
    return 'date';
  }
  return type;
};

/**
 * Returns the field names that are NOT common to all of the given datasets — a
 * field is common only when it exists in every dataset AND every dataset agrees
 * on its type category. With no datasets there is nothing to conflict with, so
 * nothing is banned.
 */
export const buildQuickFilterFieldRedList = ({
  datasets,
}: {
  datasets: FleetOps.DatasetDefinition[];
}): string[] => {
  if (datasets.length === 0) {
    return [];
  }

  const allFieldNames = new Set<string>();
  // field name -> the type category seen in each dataset that contains it
  const categoriesByField = new Map<string, string[]>();

  datasets.forEach((dataset) => {
    const seenInDataset = new Set<string>();
    dataset.fields.forEach((field) => {
      allFieldNames.add(field.field);
      if (seenInDataset.has(field.field)) {
        return;
      }
      seenInDataset.add(field.field);

      const categories = categoriesByField.get(field.field) || [];
      categories.push(getFieldTypeCategory(field.type));
      categoriesByField.set(field.field, categories);
    });
  });

  const isCommon = (fieldName: string): boolean => {
    const categories = categoriesByField.get(fieldName) || [];
    const presentInEveryDataset = categories.length === datasets.length;
    const typeCategoryAgrees = _.uniq(categories).length === 1;
    return presentInEveryDataset && typeCategoryAgrees;
  };

  return Array.from(allFieldNames).filter((fieldName) => !isCommon(fieldName));
};

export type QuickFilterIncompatibility =
  | { type: 'missing-field'; field: string; dataset: string }
  | { type: 'unsupported-plate' };

export type QuickFilterCompatibility =
  | { isCompatible: true }
  | { isCompatible: false; incompatibilities: QuickFilterIncompatibility[] };

const getPlateIncompatibilities = ({
  plate,
  datasets,
}: {
  plate: FilterPlateType;
  datasets: FleetOps.DatasetDefinition[];
}): QuickFilterIncompatibility[] => {
  if (plate.type === 'Fixed') {
    const { field, fieldType } = plate.fixedValue;
    const category = getFieldTypeCategory(fieldType);
    return datasets
      .filter((dataset) => {
        const match = dataset.fields.find((f) => f.field === field);
        return !match || getFieldTypeCategory(match.type) !== category;
      })
      .map((dataset) => ({
        type: 'missing-field',
        field,
        dataset: dataset.type,
      }));
  }

  if (plate.type === 'ExcludeTermedDrivers') {
    return datasets
      .filter((dataset) => !dataset.fields.some((f) => f.field === 'driver'))
      .map((dataset) => ({
        type: 'missing-field',
        field: 'driver',
        dataset: dataset.type,
      }));
  }

  if (plate.type === 'Dataset') {
    // Dataset-scoped saved filters are inherently single-dataset. They cannot be
    // created via the scorecard builder, but a legacy/hand-crafted doc could
    // contain one — treat it as incompatible defensively.
    return [{ type: 'unsupported-plate' }];
  }

  // Variable plates are never offered in the scorecard quick filter builder.
  return [];
};

/**
 * PURE — no context. Reports whether a quick filter is safe to apply across the
 * scorecard's current datasets, returning EVERY offending `(field, dataset)`
 * pair so the caller can list them all. With no datasets (e.g. a
 * special-metrics-only scorecard) there is nothing to conflict with.
 */
export const getQuickFilterCompatibility = ({
  quickFilter,
  datasets,
}: {
  quickFilter: LegacySavedFilter;
  datasets: FleetOps.DatasetDefinition[];
}): QuickFilterCompatibility => {
  if (datasets.length === 0) {
    return { isCompatible: true };
  }

  const plates: FilterPlateType[] = [
    ...quickFilter.drillDowns,
    ...quickFilter.scope,
  ];

  const incompatibilities = plates.flatMap((plate) =>
    getPlateIncompatibilities({ plate, datasets }),
  );

  if (incompatibilities.length > 0) {
    return { isCompatible: false, incompatibilities };
  }

  return { isCompatible: true };
};

// ['A'] -> 'A', ['A', 'B'] -> 'A and B', ['A', 'B', 'C'] -> 'A, B and C'
const formatList = (items: string[]): string => {
  if (items.length <= 1) {
    return items.join('');
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/**
 * Builds the user-facing tooltip explaining EVERY incompatibility, grouping the
 * missing-field ones by field so each lists all the datasets it is absent from.
 * Label resolvers are injected so this stays pure/testable (the hook supplies
 * the baseView- and dataset-label lookups).
 */
export const formatIncompatibilityReason = ({
  incompatibilities,
  getFieldDisplayName,
  getDatasetLabel,
}: {
  incompatibilities: QuickFilterIncompatibility[];
  getFieldDisplayName: (field: string) => string;
  getDatasetLabel: (dataset: string) => string;
}): string => {
  const sentences: string[] = [];

  const datasetsByField = new Map<string, string[]>();
  incompatibilities.forEach((incompatibility) => {
    if (incompatibility.type !== 'missing-field') {
      return;
    }
    const datasetLabels = datasetsByField.get(incompatibility.field) || [];
    const label = getDatasetLabel(incompatibility.dataset);
    if (!datasetLabels.includes(label)) {
      datasetLabels.push(label);
    }
    datasetsByField.set(incompatibility.field, datasetLabels);
  });

  datasetsByField.forEach((datasetLabels, field) => {
    const fieldName = getFieldDisplayName(field);
    const quoted = datasetLabels.map((label) => `"${label}"`);
    sentences.push(
      `This filter uses "${fieldName}", which is not available in the ${formatList(
        quoted,
      )} dataset${datasetLabels.length > 1 ? 's' : ''} used by KPIs on this list.`,
    );
  });

  const hasUnsupportedPlate = incompatibilities.some(
    (incompatibility) => incompatibility.type === 'unsupported-plate',
  );
  if (hasUnsupportedPlate) {
    sentences.push(
      'This filter uses a dataset-scoped saved filter, which cannot be applied across the multiple datasets on this list.',
    );
  }

  return sentences.join(' ');
};
